import { useEffect, useState } from 'react'
import { Provider, Root, Trigger, Content } from '@radix-ui/react-tooltip'
import { GetStaticProps, GetStaticPaths } from 'next'
import copy from 'clipboard-copy'

import Link from '@/atoms/Link/Link'
import Layout from '@/templates/Layout/Layout'
import PlatformBenefits from '@/templates/PlatformBenefits/PlatformBenefits'
import AboutPlatform from '@/templates/AboutPlatform/AboutPlatform'
import TranslationExamples from '@/templates/TranslationExamples/TranslationExamples'
import Faq from '@/templates/Faq/Faq'

import { getStrapiGraphqlData } from '@/utils/api/wrapper'
import { constructFaqJsonSchema } from '@/utils/functions/jsonSchema'
import { translationPageQuery } from '@/utils/queries/translation_page'
import {
  INSTANT_REVALIDATION,
  TEN_MINS_REVALIDATION
} from '@/utils/constants/revalidation'
import { capture404PageError } from '@/utils/functions/elk-server'

import useUpdateEffect from '@/hooks/useUpdateEffect'
import useDebounce from '@/hooks/useDebounce'

interface Props {
  currentPage: TranslationPage
  translationPageSlugs: {
    id: string
    slug: string
    source_language: {
      name: string
      iso_code: string
    }
    target_language: {
      name: string
      iso_code: string
    }
  }[]
  lang: string
}

const DEBOUNCE_TIME = 500

const IndividualTranslationPage = ({
  currentPage,
  translationPageSlugs,
  lang
}: Props) => {
  const [sourceText, setSourceText] = useState('')
  const [targetText, setTargetText] = useState('')
  const [isFetching, setIsFetching] = useState(false)

  const debouncedSourceText = useDebounce(sourceText, DEBOUNCE_TIME)

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  useEffect(() => {
    async function doOps() {
      try {
        setIsFetching(true)
        const result = await translateText(
          debouncedSourceText,
          currentPage.target_language.data.attributes.iso_code
        )
        setIsFetching(false)
        if (result) {
          setTargetText(result?.data?.translatedText)
        }
      } catch (error) {
        setIsFetching(false)
        console.error(error)
      }
    }
    if (debouncedSourceText && debouncedSourceText.length > 0) {
      doOps()
    }
    if (debouncedSourceText.length === 0) {
      setTargetText('')
    }
  }, [
    currentPage.target_language.data.attributes.iso_code,
    debouncedSourceText
  ])

  useUpdateEffect(() => {
    if (isFetching) {
      setTargetText(s => s.concat(' ....'))
    }
  }, [isFetching, sourceText, debouncedSourceText])

  const title = `Translate ${currentPage.source_language.data.attributes.name} to ${currentPage.target_language.data.attributes.name} with Free Online Translation Tool | Pepper Content`
  const description = `Translate from ${currentPage.source_language.data.attributes.name} to ${currentPage.target_language.data.attributes.name}. Translate words, phrases, texts or even paragraphs – Pepper Content will offer best ${currentPage.source_language.data.attributes.name} to ${currentPage.target_language.data.attributes.name} translation.`

  const metadata = [
    {
      property: 'og:title',
      content: title
    },
    {
      property: 'description',
      content: description
    },
    {
      property: 'og:description',
      content: description
    }
  ]

  const jsonSchema = [constructFaqJsonSchema(currentPage.faq)]

  return (
    <Layout
      jsonSchema={jsonSchema}
      title={title}
      meta={metadata}
      canonical={`/translation/${currentPage.source_language.data.attributes.iso_code}-${currentPage.slug}/`}
    >
      <section
        className="flex | items-center | flex-col | my-20 | mx-auto | px-6"
        style={{ minHeight: '40vh' }}
      >
        <header className="text-center  | mb-12">
          <h1 className="h3 font-semibold  |  mb-2">
            {currentPage.page_title}
          </h1>
          <p className="text-paragraph-2">{currentPage.hero_subtitle}</p>
        </header>

        <div
          className="display | flex | flex-wrap | gap-4 | w-full"
          style={{ maxWidth: '900px' }}
        >
          <TextField
            type="source"
            placeholder={`Type here in ${currentPage.source_language.data.attributes.name}`}
            name="source_lang"
            id="source_lang"
            onChange={e => setSourceText(e.target.value)}
            value={sourceText}
            className="flex-grow"
          />
          <TextField
            type="target"
            placeholder={`Translated to ${currentPage.target_language.data.attributes.name}`}
            name="target_lang"
            id="target_lang"
            value={targetText}
            className="flex-grow"
            readOnly
          />
        </div>
      </section>

      <div className="flex">
        <Link className="text-lg | mx-auto | btn-primary" href="/book-a-demo/">
          Talk to Sales
        </Link>
      </div>

      <PlatformBenefits
        title={currentPage.reason_title}
        description={currentPage.reason_description}
        list={currentPage.reason_cards}
      />

      <AboutPlatform
        title={`About ${currentPage.source_language.data.attributes.name} to ${currentPage.target_language.data.attributes.name} Translation Tool`}
        descriptions={[
          currentPage.about_description_1,
          currentPage.about_description_2,
          currentPage.about_description_3
        ]}
      />

      <TranslationExamples
        sourceLanguage={currentPage.source_language.data.attributes.name}
        targetLanguage={currentPage.target_language.data.attributes.name}
        translationExamples={currentPage.translation_examples}
      />

      {currentPage.footer_content && (
        <section className="max-width-container  |  py-16">
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: currentPage.footer_content }}
          />
        </section>
      )}

      <Faq faqs={currentPage.faq} />

      <section className="bg-gray-90">
        <div className="max-desktop-width  |  mx-auto py-16 px-5 sm:px-10 lg:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {translationPageSlugs.map(page => {
              if (page.slug === currentPage.slug) return null
              return (
                <Link
                  key={`translation-page-${page.id}`}
                  href={`/translation/${page.source_language.iso_code}-${page.slug}/`}
                >
                  <span className="text-paragraph-4 | inline-block text-gray-40">
                    {page.source_language?.name} to {page.target_language?.name}
                    &nbsp;Translation
                  </span>
                </Link>
              )
            })}
          </div>
        </div>
      </section>
    </Layout>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const slug = params?.slug as string
  const slugArray = slug.split('-')
  let lang = ''
  let reqSlug = ''

  // Case 5: "zh-CN-chinese-to-uyghur"  // 4 letter with single hyphen separated isoCode & single word source language
  // Case 1: "en-english-to-afrikaans" // 2 letter isoCode & single word source language
  // Case 2: "hmn-hmong-to-afrikaans" // 3 letter isoCode & single word source language
  // Case 3: "lo-lao-to-afrikaans" // 2 letter isoCode & single word 3 letter source language
  // Case 4: "zh-chinese-traditional-to-uyghur"  // 4 letter with single hyphen separated isoCode & double word with single hyphen separated source language

  // spliting isoCode and source-to-target slug from requestSlug considering above cases.
  slugArray.forEach((s, index) => {
    ;(slugArray.length === 4 && s.length <= 3 && index === 0) ||
    (slugArray.length > 4 && s.length <= 3 && index <= 1)
      ? (lang = lang === '' ? lang.concat(s) : lang.concat('-' + s))
      : (reqSlug = reqSlug === '' ? reqSlug.concat(s) : reqSlug.concat('-' + s))
  })

  const result = await getStrapiGraphqlData<TranslationPageQuery>(
    translationPageQuery.loc?.source.body,
    { slug: reqSlug }
  )

  const page = result.currentPage.data[0]
  const links = result.translationPages.data

  if (!page) {
    capture404PageError(`/translation/${reqSlug}/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  if (
    page.attributes.source_language.data.attributes.iso_code !== lang &&
    page.attributes.target_language.data.attributes.iso_code !== lang
  ) {
    capture404PageError(`/translation/${reqSlug}/`)
    return { notFound: true, revalidate: INSTANT_REVALIDATION }
  }

  return {
    props: {
      lang,
      currentPage: page.attributes,
      translationPageSlugs: links.map(link => ({
        id: link.id,
        slug: link.attributes.slug,
        source_language: link.attributes.source_language.data.attributes,
        target_language: link.attributes.target_language.data.attributes
      }))
    },
    revalidate: TEN_MINS_REVALIDATION
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  }
}

async function translateText(text: string, target: string) {
  if (!process.env.NEXT_PUBLIC_TRANSLATION_SERVICE_API_KEY)
    throw new Error('API key not present')

  const url = new URL(
    'https://translation.googleapis.com/language/translate/v2'
  )
  url.searchParams.append('q', text)
  url.searchParams.append('target', target)
  url.searchParams.append(
    'key',
    process.env.NEXT_PUBLIC_TRANSLATION_SERVICE_API_KEY
  )
  url.searchParams.append('format', 'text')

  const response = await fetch(url.href, {
    method: 'POST'
  })
  const result = await response.json()
  if (response.ok) {
    return {
      data: result.data.translations[0],
      status: 'success'
    }
  } else throw new Error(result.error)
}

type TextAreaType = 'source' | 'target'

interface TextFieldProps
  extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  disabled?: boolean
  value: string
  className: string
  type: TextAreaType
}

const TextField: React.FC<TextFieldProps> = ({
  className,
  type,
  placeholder,
  disabled = false,
  onChange,
  value,
  ...rest
}) => {
  const [words, setWords] = useState<number>(0)
  const [characters, setCharacters] = useState<number>(0)

  useEffect(() => {
    setWords(countWords(value))
    setCharacters(value.length)
  }, [value])

  return (
    <div className={`flex | flex-col | shadow-md | px-4 | py-2 | ${className}`}>
      <div className="top | flex | justify-between | text-small | mb-4">
        <div className="stats | flex">
          <span className="words | mr-2">{`${words} words`}</span>
          <span className="separator">|</span>
          <span className="characters | ml-2">{`${characters} characters`}</span>
        </div>
        <span className="limit">{`${characters} / 500`}</span>
      </div>
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={12}
        maxLength={500}
        style={{
          resize: 'none',
          outline: 'none',
          minWidth: '200px',
          maxWidth: '500px'
        }}
        {...rest}
      />
      {type === 'target' && (
        <div className="actions | tw-flex | tw-justify-end">
          <Provider>
            <Root delayDuration={0}>
              <Trigger
                className="tw-border-none | tw-outline-none"
                onClick={async () => {
                  try {
                    await copy(value)
                  } catch (error) {
                    console.error(error)
                  }
                }}
              >
                <ClipboardLogo className="tw-h-5 | tw-w-5 | tw-text-gray-50 | hover:tw-text-gray-70 | tw-duration-300" />
              </Trigger>
              <Content className="tw-px-2 | tw-py-3 | tw-bg-white | tw-text-small | tw-shadow-md | tw-rounded-md">
                Copy to Clipboard
              </Content>
            </Root>
          </Provider>
        </div>
      )}
    </div>
  )
}

function countWords(s: string): number {
  s = s.replace(/(^\s*)|(\s*$)/gi, '') //exclude  start and end white-space
  s = s.replace(/[ ]{2,}/gi, ' ') //2 or more space to 1
  s = s.replace(/\n /, '\n') // exclude newline with a start spacing
  return s.split(' ').filter(str => str != '').length
}

const ClipboardLogo = (
  props: React.HTMLAttributes<HTMLOrSVGElement>
): JSX.Element => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-6 w-6"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
    />
  </svg>
)

export default IndividualTranslationPage
